import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import * as Sentry from '@sentry/react';

// Make search diagnostic available in browser console
import './utils/searchDiagnostic';

import {
  hydrateRuntimeConfigFromCache,
  persistRuntimeConfigSnapshot,
} from './pwa/runtimeConfigCache';
import { configureErrorHandling } from './utils/errorHandler';
import { registerProjectMeatsServiceWorker } from './pwa/registerServiceWorker';
import {
  getChunkRecoveryMessage,
  importWithChunkRecovery,
  isChunkLoadError,
  reloadApplication,
} from './utils/chunkLoadRecovery';
import { initSentry } from './utils/sentry';
import { initGlobalErrorHandlers } from './utils/globalErrorHandlers';
import notify from './utils/notify';
import { logger } from './utils/logger';
import { initPerformanceReporter } from './utils/performanceReporter';
import { runSessionGuard } from './utils/sessionGuard';

// Lazy-loaded so it does not bloat the initial bundle.
const AppUpdateBannerAsync = React.lazy(
  () => import('./components/Shared/AppUpdateBanner/AppUpdateBanner')
);

const liveRuntimeConfig = window.ENV ? { ...window.ENV } : undefined;

hydrateRuntimeConfigFromCache();
initSentry();
initGlobalErrorHandlers('frontend');
configureErrorHandling({
  logError: ({ context, error, logMessage, metadata }) => {
    logger.error(
      logMessage,
      {
        component: context ?? 'error-handler',
        metadata,
      },
      error
    );
  },
  notifyError: ({ message }) => {
    notify.error(message);
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('ProjectMeats root element not found.');
}

const root = ReactDOM.createRoot(rootElement);

async function bootstrapApplication(): Promise<void> {
  persistRuntimeConfigSnapshot(liveRuntimeConfig);

  // ── Session Guard ────────────────────────────────────────────────────────
  // Detect "user in localStorage but no tokens" broken state BEFORE any
  // React component mounts. Without this, every context fires API calls
  // that all return 403 (anonymous request) and the app shows a permanently
  // broken state with no self-recovery path.
  if (!runSessionGuard()) {
    // Redirect has been initiated — skip rendering to avoid 403 storms.
    return;
  }
  // ── /Session Guard ───────────────────────────────────────────────────────

  try {
    const [{ default: App }] = await Promise.all([
      importWithChunkRecovery(() => import('./App'), 'App'),
    ]);

    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong.</div>}>
          <App />
        </Sentry.ErrorBoundary>
      </React.StrictMode>
    );

    registerProjectMeatsServiceWorker(
      import.meta.env,
      window.navigator,
      undefined,
      (updateSW) => {
        // A new service worker is waiting. Mount a prompt banner instead of
        // auto-reloading. The banner unmounts itself after user interaction.
        const bannerRoot = document.createElement('div');
        bannerRoot.id = 'pwa-update-banner';
        document.body.appendChild(bannerRoot);

        const bannerReactRoot = ReactDOM.createRoot(bannerRoot);

        const unmount = () => {
          bannerReactRoot.unmount();
          document.body.removeChild(bannerRoot);
        };

        const handleRefresh = () => {
          unmount();
          updateSW(true);
        };

        bannerReactRoot.render(
          <React.Suspense fallback={null}>
            <AppUpdateBannerAsync onRefresh={handleRefresh} onDismiss={unmount} />
          </React.Suspense>
        );
      }
    );
    initPerformanceReporter();
  } catch (error) {
    const chunkRecoveryMessage =
      getChunkRecoveryMessage(error) ||
      'The application failed to start. Reload the page to try again.';

    root.render(
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'rgb(var(--color-bg-secondary))',
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: '100%',
            border: '1px solid rgb(var(--color-border))',
            borderRadius: 16,
            background: 'rgb(var(--color-bg-primary))',
            padding: 24,
            boxSizing: 'border-box',
          }}
        >
          <h1 style={{ marginTop: 0 }}>Unable to load Meats Central</h1>
          <p style={{ color: 'rgb(var(--color-text-secondary))' }}>{chunkRecoveryMessage}</p>
          <button
            type="button"
            onClick={() =>
              reloadApplication(
                isChunkLoadError(error) ? 'bootstrap-chunk-retry' : 'bootstrap-retry'
              )
            }
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              background: 'rgb(var(--color-primary))',
              color: 'rgb(var(--color-primary-foreground))',
              cursor: 'pointer',
            }}
          >
            Reload application
          </button>
        </div>
      </div>
    );
  }
}

void bootstrapApplication();
